/**
 * Functionality to format data for data entries.
 */

import _ from "lodash";

/**============================================================================*/

type Field = { type: string; value: string };

class EntryFormatter {
  /**
   * Makes given literal URL friendly, which means only [a-zA-Z0-9_.-] is
   * allowed in the literal, all other glyphs are filtered out.
   *
   * Note: " " is converted into "-"
   *
   * @param literal - Literal to convert
   * @returns {String}
   */
  static makeURLFriendly(literal: string) {
    if (!_.isString(literal)) {
      return "";
    }

    return literal
      .split("")
      .map((c) => {
        if (c >= "a" && c <= "z") {
          return c;
        } else if (c >= "A" && c <= "Z") {
          return c;
        } else if (c >= "0" && c <= "9") {
          return c;
        } else if (c === "-" || c === "_" || c === ".") {
          return c;
        } else if (c === " ") {
          return "-";
        } else if (c === "ä" || c === "å") {
          return "a";
        } else if (c === "ö") {
          return "o";
        } else {
          return null;
        }
      })
      .filter((c) => c !== null)
      .join("");
  }

  /**
   * Retrieves the value of the first field in the given array/object of
   * fields.
   *
   * Note: returns an empty string if given fields is invalid, empty or
   * the first field is invalid.
   *
   * Note: Image fields are considered invalid since we can't convert an
   * image to a literal representation.
   *
   * @param fields - Data entry fields
   * @returns {string}
   */
  static getFirstFieldValue(fields: Field[] | Record<any, Field>) {
    let first = null;

    if (_.isArray(fields)) {
      first = _.head(fields);
    } else if (_.isObject(fields)) {
      first = Object.values(fields)[0];
    } else {
      return "";
    }

    if (!_.isObject(first) || !_.has(first, "value") || !_.has(first, "type")) {
      return "";
    }

    if (first.type === "image") {
      return "";
    }

    return first.value;
  }

  /**
   * Creates a suitable filename for exports of a single data entry from the
   * given inventory title and entry fields.
   *
   * Note: returns an empty string if the given inputs are invalid.
   *
   * @param prefix - Value to put as prefix of the filename
   * @param title - Title of the inventory
   * @param fields - Data entry fields
   * @param extension - File extension to use
   * @returns {String}
   */
  static generateFilename(prefix: string, title: string, fields: Field[] | Record<any, Field>, extension: string) {
    const firstValue = EntryFormatter.getFirstFieldValue(fields);
    const parts = [];

    if (_.isString(prefix) && prefix !== "") {
      parts.push(prefix);
    }

    if (_.isString(title) && title !== "") {
      parts.push(title);
    }

    if (_.isString(firstValue) && firstValue !== "") {
      parts.push(firstValue);
    }

    if (parts.length < 1) {
      return "";
    }

    const date = new Date();
    const dateLit = [
      date.getFullYear(),
      date.getMonth() + 1, // Months starts at 0
      date.getDate(),
      date.getHours() + ":" + date.getMinutes() + ":" + date.getSeconds(),
    ].join("-");

    parts.push(dateLit);

    if (!_.isString(extension) || extension === "") {
      extension = ".txt";
    }

    return EntryFormatter.makeURLFriendly(parts.join("_") + extension);
  }
}

export { EntryFormatter };
